import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { ReactNode, useState } from 'react'
import { primaryButtonCss } from '../components/Button'
import FormFieldText from '../components/FormFieldText'
import FormResult from '../components/FormResult'
import RecaptchaContainer from '../components/RecaptchaContainer'
import RecaptchaLoader from '../components/RecaptchaLoader'
import ChevronRight from '../icons/ChevronRight.svg'
import { useRecaptchaForm } from '../utils/useRecaptchaForm'

const FormControl = styled('div', {
  shouldForwardProp: (prop) => prop !== 'fullWidth',
})<{ fullWidth?: boolean }>(({ fullWidth, theme }) => [
  css`
    display: block;

    @media screen and (min-width: ${theme.breakpoints.tablet}px) {
      display: inline-block;
      width: ${fullWidth ? '100%' : '50%'};
      vertical-align: top;
    }
  `,
  !fullWidth &&
    css`
      @media screen and (min-width: ${theme.breakpoints.tablet}px) {
        :nth-of-type(odd) {
          padding-right: ${theme.spacing.x3 / 2}px;
        }
        :nth-of-type(even) {
          padding-left: ${theme.spacing.x3 / 2}px;
        }
      }
    `,
])
const Title = styled.h2(
  ({ theme }) => css`
    ${theme.text.heading2(theme)}
    margin-bottom: ${theme.spacing.x3}px;
  `,
)
const Text = styled.div(
  ({ theme }) => css`
    display: none;
    margin-bottom: ${theme.spacing.x3}px;

    @media screen and (min-width: ${theme.breakpoints.tablet}px) {
      display: block;
    }
  `,
)
const Section = styled.section(
  ({ theme }) => css`
    padding: ${theme.spacing.x5}px ${theme.spacing.x2}px;

    @media screen and (min-width: ${theme.breakpoints.tablet}px) {
      padding: ${theme.spacing.x10}px 0;
      margin: 0 auto;
      width: ${theme.spacing.containerWidth}px;
    }
  `,
)
const Recaptcha = styled.div(
  ({ theme }) => css`
    background: ${theme.colors.neutral1};
    font-size: 11px;
    line-height: 1.45;
    padding: ${theme.spacing.x2}px;
    margin-bottom: ${theme.spacing.x3}px;

    p {
      margin: 0;
    }

    @media screen and (min-width: ${theme.breakpoints.tablet}px) {
      font-size: 14px;
      line-height: 1.57;
      margin-bottom: ${theme.spacing.x3}px;
      padding: ${theme.spacing.x3}px;
    }
  `,
)
const Privacy = styled.div(
  ({ theme }) =>
    css`
      align-items: center;
      display: flex;
      font-size: 12px;
      margin-bottom: ${theme.spacing.x2}px;

      @media screen and (min-width: ${theme.breakpoints.tablet}px) {
        font-size: 16px;
        margin-bottom: ${theme.spacing.x3}px;
      }
    `,
)
const Submit = styled.button(
  ({ theme }) => css`
    ${primaryButtonCss(theme)}
    font-weight: bold;
    line-height: 1.57;
    padding: ${theme.spacing.x2}px;
  `,
)

type formField = {
  errorMessage?: string | null
  id: string
  label?: string | null
  name?: string | null
  minLength?: number
  maxLength?: number
  placeholder?: string | null
  pattern?: string | null
  required?: boolean
  variation?: string | null
}

interface Props {
  formId: string
  name?: string | null
  text: ReactNode
  recaptcha: ReactNode
  privacyPolicy: ReactNode
  error?: {
    title?: string
    text?: string
    buttonText?: string
  }
  success?: {
    title?: string
    text?: string
    link: {
      text?: string
      href: string
      target?: string
    }
  }
  submitText?: string | null
  formFields: formField[]
}

interface IFormInputs {
  [key: string]: string | boolean | number
}

const FormSection = ({
  formId,
  name,
  text,
  recaptcha,
  privacyPolicy,
  error,
  success,
  submitText,
  formFields,
}: Props) => {
  const {
    recaptchaElementId,
    isLoading,
    getRecaptchaToken,
    register,
    handleSubmit,
    trigger,
    formState: { errors },
  } = useRecaptchaForm<IFormInputs>()

  const [isSubmitting, setIsSubmitting] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [greatSuccess, setGreatSuccess] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [formError, setFormError] = useState(false)

  const onSubmit = async (data: IFormInputs) => {
    setIsSubmitting(true)

    try {
      const recaptchaResponse = await getRecaptchaToken()

      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          formId,
          fields: data,
          ...recaptchaResponse,
        }),
      })
      const result = await response.json()

      if (!result.success) {
        setFormError(true)
        return
      }
      setGreatSuccess(true)
    } catch (err) {
      setFormError(true)
      throw err
    } finally {
      setIsSubmitting(false)
    }
  }

  if (greatSuccess) {
    return (
      <Section>
        <Title>{name}</Title>
        <Text>{text}</Text>
        <FormResult type="success" {...success} />
      </Section>
    )
  }

  if (formError) {
    return (
      <Section>
        <Title>{name}</Title>
        <Text>{text}</Text>
        <FormResult
          type="error"
          {...error}
          onClick={() => setFormError(false)}
        />
      </Section>
    )
  }

  return (
    <Section>
      <Title>{name}</Title>
      <Text>{text}</Text>
      <form onSubmit={handleSubmit(onSubmit)}>
        {formFields.map(({ id, label, name, variation, ...rest }) => {
          if (!name || !label) return null
          return (
            <FormControl key={id} fullWidth={variation === 'textarea'}>
              <FormFieldText
                {...rest}
                label={label}
                name={name}
                register={register}
                id={id}
                errors={errors}
                trigger={trigger}
                variation={variation}
              />
            </FormControl>
          )
        })}
        <Recaptcha>{recaptcha}</Recaptcha>
        <RecaptchaLoader />
        <RecaptchaContainer id={recaptchaElementId} />
        <Privacy>{privacyPolicy}</Privacy>
        <Submit disabled={isSubmitting || isLoading} type="submit">
          {submitText}
          <ChevronRight />
        </Submit>
      </form>
    </Section>
  )
}

export default FormSection
